import { useState, useEffect, useRef, useMemo } from "react";
import { Plus, Trash2, Upload, Download, TableProperties, Filter, X, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRBAC } from "@/hooks/useRBAC";
import { pricing as pricingApi, equipment as equipmentApi } from "@/lib/api";

const STORAGE_KEY = "strataworks-consumable-pricing";
const ASSETS_STORAGE_KEY = "strataworks-assets";

type AssetType = "drill-rig" | "excavator" | "dozer" | "grader" | "truck" | "other";

interface Asset {
  id: string;
  type: AssetType;
  model: string;
  assetNumber: string;
}

const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  "drill-rig": "Drill Rig",
  "excavator": "Excavator",
  "dozer": "Dozer",
  "grader": "Grader",
  "truck": "Truck",
  "other": "Other",
};

const ASSET_MODELS: Record<AssetType, string[]> = {
  "drill-rig": ["D65", "MD6250"],
  "excavator": [],
  "dozer": [],
  "grader": [],
  "truck": [],
  "other": [],
};

const EMPTY_ASSET_FORM: Omit<Asset, "id"> = { type: "drill-rig", model: "", assetNumber: "" };

function loadAssetsLocal(): Asset[] {
  try { return JSON.parse(localStorage.getItem(ASSETS_STORAGE_KEY) ?? "[]"); }
  catch { return []; }
}

type ConsumableType = "drill-bit" | "hammer" | "drill-rod" | "drive-chuck" | "retaining-ring" | "sub";

interface ConsumablePrice {
  id: string;
  type: ConsumableType;
  subType: string;
  faceDesign: string;
  retrac: boolean;
  diameter: string;
  description: string;
  manufacturer: string;
  unitCost: string;
}

const EMPTY_FORM: Omit<ConsumablePrice, "id"> = {
  type: "drill-bit",
  subType: "",
  faceDesign: "",
  retrac: false,
  diameter: "",
  description: "",
  manufacturer: "",
  unitCost: "",
};

const TYPE_LABELS: Record<ConsumableType, string> = {
  "drill-bit":      "Drill Bit",
  "hammer":         "Hammer",
  "drill-rod":      "Drill Rod",
  "drive-chuck":    "Drive Chuck",
  "retaining-ring": "Retaining Ring",
  "sub":            "Sub",
};

const BIT_TYPES = ["DTH", "Rotary", "Top Hammer"];
const BIT_SIZES_BY_TYPE: Record<string, string[]> = {
  "DTH":         ["127mm", "140mm", "165mm", "203mm", "229mm"],
  "Rotary":      ["200mm", "203mm", "229mm"],
  "Top Hammer":  ["89mm", "102mm", "127mm"],
};

const HAMMER_SIZES = ['3.5" (89mm)', '4" (102mm)', '4.5" (115mm)', '5" (127mm)', '6" (152mm)', '7" (178mm)', '8" (203mm)', '10" (254mm)', '12" (305mm)'];
const FACE_DESIGNS = ["Flat Face (FF)", "Dual Gauge Row Flat Face (DGR FF)", "Drop Centre (DC)", "Concave (CC)", "Convex (CX)"];

const ROD_TYPES = ["Standard Rod", "Extension Rod", "Bit Sub", "Crossover Sub", "Top Sub", "Back Adapter"];
const MANUFACTURERS = ["AMS", "StrataWorks", "TerraTek", "Epiroc", "Mincon", "Sandvik", "Robit"];

function loadPricingLocal(): ConsumablePrice[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
  } catch {
    return [];
  }
}

const CSV_HEADERS = ["type", "subType", "faceDesign", "retrac", "diameter", "description", "manufacturer", "unitCost"];

function downloadPricingTemplate() {
  const sample: Omit<ConsumablePrice, "id">[] = [
    { type: "drill-bit", subType: "DTH",        faceDesign: "Flat Face (FF)", retrac: false, diameter: "165mm", description: "165mm DTH FF Button Bit", manufacturer: "Epiroc",  unitCost: "850"  },
    { type: "drill-bit", subType: "Top Hammer", faceDesign: "Drop Centre (DC)", retrac: true, diameter: "102mm", description: "102mm Top Hammer DC Retrac", manufacturer: "Sandvik", unitCost: "320" },
    { type: "hammer",    subType: '6" (152mm)',  faceDesign: "",               retrac: false, diameter: "165mm", description: "6 inch DTH Hammer",           manufacturer: "Mincon",  unitCost: "4200" },
    { type: "drill-rod", subType: "Standard Rod",faceDesign: "",               retrac: false, diameter: "3.6m",  description: "T51 Extension Rod 3.6m",      manufacturer: "Sandvik", unitCost: "780"  },
  ];
  const rows = [
    CSV_HEADERS.join(","),
    ...sample.map(r => CSV_HEADERS.map(h => {
      const v = String(r[h as keyof typeof r] ?? "");
      return v.includes(",") ? `"${v}"` : v;
    }).join(",")),
  ];
  const blob = new Blob([rows.join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "consumable-pricing-template.csv";
  a.click();
  URL.revokeObjectURL(a.href);
}

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (!inQuotes && cur === "") {
        inQuotes = true; // opening quote at start of field
      } else if (inQuotes && line[i + 1] === '"') {
        cur += '"'; i++;  // escaped quote inside quoted field
      } else if (inQuotes) {
        inQuotes = false; // closing quote
      } else {
        cur += ch; // mid-field quote (e.g. 6") — treat as literal
      }
    } else if (ch === "," && !inQuotes) {
      result.push(cur.trim());
      cur = "";
    } else {
      cur += ch;
    }
  }
  result.push(cur.trim());
  return result;
}

const TYPE_NORMALISE: Record<string, ConsumableType> = {
  "drill-bit": "drill-bit", "drill bit": "drill-bit", "drillbit": "drill-bit", "bit": "drill-bit",
  "hammer": "hammer", "dth hammer": "hammer", "dth": "hammer",
  "drill-rod": "drill-rod", "drill rod": "drill-rod", "drillrod": "drill-rod", "rod": "drill-rod",
  "drive-chuck": "drive-chuck", "drive chuck": "drive-chuck", "chuck": "drive-chuck",
  "retaining-ring": "retaining-ring", "retaining ring": "retaining-ring", "ring": "retaining-ring",
  "sub": "sub",
};

function parsePricingCSV(text: string): { entries: Omit<ConsumablePrice, "id">[]; errors: string[] } {
  // strip BOM if present
  const clean = text.replace(/^\uFEFF/, "");
  const lines = clean.split(/\r?\n|\r/).filter(l => l.trim());

  if (lines.length < 2) return { entries: [], errors: ["CSV has no data rows."] };
  const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().trim());
  const idx = (name: string) => headers.indexOf(name.toLowerCase());
  const entries: Omit<ConsumablePrice, "id">[] = [];
  const errors: string[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = parseCSVLine(lines[i]);
    const get = (name: string) => (cols[idx(name)] ?? "").trim();
    const rawType = get("type").toLowerCase();
    const type = TYPE_NORMALISE[rawType];

    if (!type) {
      errors.push(`Row ${i + 1}: unrecognised type "${get("type")}"`);
      continue;
    }
    entries.push({
      type,
      subType:      get("subType"),
      faceDesign:   get("faceDesign"),
      retrac:       get("retrac").toLowerCase() === "true",
      diameter:     get("diameter"),
      description:  get("description"),
      manufacturer: get("manufacturer"),
      unitCost:     get("unitCost"),
    });
  }
  return { entries, errors };
}

export default function Settings() {
  const { can } = useRBAC();
  const [pricing, setPricing] = useState<ConsumablePrice[]>(loadPricingLocal);

  useEffect(() => {
    pricingApi.list().then((rows) => {
      setPricing(rows as unknown as ConsumablePrice[]);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
    }).catch(() => {});
  }, []);
  const [form, setForm] = useState<Omit<ConsumablePrice, "id">>(EMPTY_FORM);
  const [assets, setAssets] = useState<Asset[]>(loadAssetsLocal);

  useEffect(() => {
    equipmentApi.list().then((rows) => {
      const mapped = rows.map((r) => ({ id: r.id, type: r.type as AssetType, model: r.model, assetNumber: r.assetNumber }));
      setAssets(mapped);
      localStorage.setItem(ASSETS_STORAGE_KEY, JSON.stringify(mapped));
    }).catch(() => {});
  }, []);
  const [assetForm, setAssetForm] = useState<Omit<Asset, "id">>(EMPTY_ASSET_FORM);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importMessage, setImportMessage] = useState<{ text: string; error: boolean } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Pricing table filters
  const [filterType, setFilterType] = useState<string>("all");
  const [filterSubType, setFilterSubType] = useState<string>("all");
  const [filterDiameter, setFilterDiameter] = useState<string>("all");
  const [filterManufacturer, setFilterManufacturer] = useState<string>("all");
  const [filterSearch, setFilterSearch] = useState("");
  const [sortByCost, setSortByCost] = useState(false);

  const filterOptions = useMemo(() => {
    const types = [...new Set(pricing.map(p => p.type))].sort();
    const subTypes = [...new Set(pricing.filter(p => filterType === "all" || p.type === filterType).map(p => p.subType).filter(Boolean))].sort();
    const diameters = [...new Set(pricing.filter(p => filterType === "all" || p.type === filterType).map(p => p.diameter).filter(Boolean))].sort();
    const manufacturers = [...new Set(pricing.map(p => p.manufacturer).filter(Boolean))].sort();
    return { types, subTypes, diameters, manufacturers };
  }, [pricing, filterType]);

  const filteredPricing = useMemo(() => {
    const search = filterSearch.toLowerCase();
    const result = pricing.filter(p => {
      if (filterType !== "all" && p.type !== filterType) return false;
      if (filterSubType !== "all" && p.subType !== filterSubType) return false;
      if (filterDiameter !== "all" && p.diameter !== filterDiameter) return false;
      if (filterManufacturer !== "all" && p.manufacturer !== filterManufacturer) return false;
      if (search && !p.description.toLowerCase().includes(search) && !p.manufacturer.toLowerCase().includes(search)) return false;
      return true;
    });
    if (sortByCost) {
      result.sort((a, b) => Number(a.unitCost) - Number(b.unitCost));
    }
    return result;
  }, [pricing, filterType, filterSubType, filterDiameter, filterManufacturer, filterSearch, sortByCost]);

  const hasActiveFilters = filterType !== "all" || filterSubType !== "all" || filterDiameter !== "all" || filterManufacturer !== "all" || filterSearch !== "";

  const clearFilters = () => {
    setFilterType("all");
    setFilterSubType("all");
    setFilterDiameter("all");
    setFilterManufacturer("all");
    setFilterSearch("");
    setSortByCost(false);
  };

  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(pricing)); }, [pricing]);
  useEffect(() => { localStorage.setItem(ASSETS_STORAGE_KEY, JSON.stringify(assets)); }, [assets]);

  if (!can("manage:org")) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center">
        <p className="text-muted-foreground">You don't have permission to view this page.</p>
      </div>
    );
  }

  const handleAdd = async () => {
    if (!form.description || !form.unitCost) return;
    try {
      const row = await pricingApi.create(form);
      setPricing((prev) => [...prev, row as unknown as ConsumablePrice]);
      setForm(EMPTY_FORM);
    } catch {
      toast.error("Failed to save pricing");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await pricingApi.remove(id);
      setPricing((prev) => prev.filter((p) => p.id !== id));
    } catch {
      toast.error("Failed to delete pricing");
    }
  };

  const handleBulkImport = async () => {
    if (!selectedFile) return;
    const text = await selectedFile.text();
    const { entries, errors } = parsePricingCSV(text);
    setShowBulkUpload(false);
    setSelectedFile(null);
    if (entries.length > 0) {
      try {
        const rows = await pricingApi.bulkUpsert(entries);
        setPricing(rows as unknown as ConsumablePrice[]);
        const msg = errors.length > 0
          ? `Imported ${entries.length} entries. ${errors.length} row(s) skipped: ${errors[0]}`
          : `Imported ${entries.length} entr${entries.length > 1 ? "ies" : "y"} successfully.`;
        setImportMessage({ text: msg, error: false });
      } catch {
        setImportMessage({ text: "Failed to save pricing to server.", error: true });
      }
    } else {
      setImportMessage({ text: errors.join(" | ") || "No valid entries found.", error: true });
    }
    setTimeout(() => setImportMessage(null), 15000);
  };

  const handleAddAsset = async () => {
    if (!assetForm.assetNumber) return;
    try {
      const row = await equipmentApi.create({ type: assetForm.type, model: assetForm.model, assetNumber: assetForm.assetNumber });
      setAssets((prev) => [...prev, { id: row.id, type: row.type as AssetType, model: row.model, assetNumber: row.assetNumber }]);
      setAssetForm(EMPTY_ASSET_FORM);
    } catch {
      toast.error("Failed to save asset");
    }
  };

  const handleDeleteAsset = async (id: string) => {
    try {
      await equipmentApi.remove(id);
      setAssets((prev) => prev.filter((a) => a.id !== id));
    } catch {
      toast.error("Failed to delete asset");
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Settings</h1>
        <p className="text-muted-foreground">Manage organisation configuration</p>
      </div>

      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="text-foreground">Consumable Pricing</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Set unit costs for consumables to enable cost tracking across shift reports.
              </p>
              {importMessage && (
                <p className={`text-xs mt-2 ${importMessage.error ? "text-destructive" : "text-green-500"}`}>
                  {importMessage.text}
                </p>
              )}
            </div>
            <Button variant="outline" size="sm" className="shrink-0" onClick={() => setShowBulkUpload(true)}>
              <Upload className="h-3.5 w-3.5 mr-1.5" />
              Bulk Upload
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* Add form */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 rounded-lg border border-border bg-background">

            {/* Type */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Type</Label>
              <Select
                value={form.type}
                onValueChange={(v) => setForm((f) => ({ ...f, type: v as ConsumableType, subType: "", diameter: "" }))}
              >
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Sub-type — changes based on type */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">
                {form.type === "drill-bit" ? "Bit Type" : form.type === "hammer" ? "Hammer Size" : "Rod / Sub Type"}
              </Label>
              <Select
                value={form.subType}
                onValueChange={(v) => setForm((f) => ({ ...f, subType: v, diameter: "", faceDesign: "", retrac: false }))}
              >
                <SelectTrigger className="h-9"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {form.type === "drill-bit" && BIT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  {form.type === "hammer"    && HAMMER_SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  {form.type === "drill-rod" && ROD_TYPES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Size — predefined for bits, free text for rods */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">
                {form.type === "hammer" ? "Bit Diameter" : "Size"}
              </Label>
              {form.type === "drill-bit" ? (
                <Select value={form.diameter} onValueChange={(v) => setForm((f) => ({ ...f, diameter: v }))}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Select size…" /></SelectTrigger>
                  <SelectContent>
                    {(BIT_SIZES_BY_TYPE[form.subType] ?? []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  className="h-9"
                  placeholder={form.type === "hammer" ? "e.g. 165mm" : "e.g. 3.6m"}
                  value={form.diameter}
                  onChange={(e) => setForm((f) => ({ ...f, diameter: e.target.value }))}
                />
              )}
            </div>

            {/* Face Design — DTH and Top Hammer only */}
            {form.type === "drill-bit" && (form.subType === "DTH" || form.subType === "Top Hammer") && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Face Design</Label>
                <Select value={form.faceDesign} onValueChange={(v) => setForm((f) => ({ ...f, faceDesign: v }))}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {FACE_DESIGNS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Retrac / Standard — Top Hammer only */}
            {form.type === "drill-bit" && form.subType === "Top Hammer" && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Thread Type</Label>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, retrac: !f.retrac }))}
                  className="w-full flex items-center gap-3 h-9 rounded-md border border-input bg-background px-3 text-sm hover:border-primary/50 transition-colors"
                >
                  <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${form.retrac ? "bg-primary border-primary" : "border-muted-foreground"}`}>
                    {form.retrac && <span className="text-primary-foreground text-xs font-bold">✓</span>}
                  </span>
                  <span className="text-foreground">{form.retrac ? "Retrac" : "Standard"}</span>
                </button>
              </div>
            )}

            {/* Manufacturer */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Manufacturer</Label>
              <Select value={form.manufacturer} onValueChange={(v) => setForm((f) => ({ ...f, manufacturer: v }))}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {MANUFACTURERS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Description</Label>
              <Input
                className="h-9"
                placeholder="e.g. 165mm FF Button Bit"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>

            {/* Unit Cost */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Unit Cost ($)</Label>
              <Input
                className="h-9"
                placeholder="e.g. 850"
                type="number"
                min="0"
                value={form.unitCost}
                onChange={(e) => setForm((f) => ({ ...f, unitCost: e.target.value }))}
              />
            </div>

            <div className="md:col-span-3 flex justify-end">
              <Button size="sm" onClick={handleAdd} disabled={!form.description || !form.unitCost}>
                <Plus className="h-4 w-4 mr-2" />
                Add Entry
              </Button>
            </div>
          </div>

          {/* Filter bar */}
          {pricing.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg border border-border bg-muted/20">
              <Filter className="h-4 w-4 text-muted-foreground shrink-0" />
              <Select value={filterType} onValueChange={(v) => { setFilterType(v); setFilterSubType("all"); setFilterDiameter("all"); }}>
                <SelectTrigger className="h-8 w-[130px] text-xs"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {filterOptions.types.map(t => <SelectItem key={t} value={t}>{TYPE_LABELS[t as ConsumableType] ?? t}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterSubType} onValueChange={setFilterSubType}>
                <SelectTrigger className="h-8 w-[130px] text-xs"><SelectValue placeholder="Sub-type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sub-types</SelectItem>
                  {filterOptions.subTypes.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterDiameter} onValueChange={setFilterDiameter}>
                <SelectTrigger className="h-8 w-[110px] text-xs"><SelectValue placeholder="Size" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sizes</SelectItem>
                  {filterOptions.diameters.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterManufacturer} onValueChange={setFilterManufacturer}>
                <SelectTrigger className="h-8 w-[130px] text-xs"><SelectValue placeholder="Manufacturer" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Suppliers</SelectItem>
                  {filterOptions.manufacturers.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input
                className="h-8 w-[160px] text-xs"
                placeholder="Search description..."
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
              />
              <Button
                variant={sortByCost ? "secondary" : "ghost"}
                size="sm"
                className={`h-8 px-2 text-xs ${sortByCost ? "text-foreground" : "text-muted-foreground"}`}
                onClick={() => setSortByCost(v => !v)}
              >
                <ArrowUpDown className="h-3.5 w-3.5 mr-1" />
                Cheapest first
              </Button>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-muted-foreground" onClick={clearFilters}>
                  <X className="h-3.5 w-3.5 mr-1" />
                  Clear
                </Button>
              )}
              <span className="text-xs text-muted-foreground ml-auto">
                {filteredPricing.length} of {pricing.length} entries
              </span>
            </div>
          )}

          {/* Pricing table */}
          {pricing.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-muted-foreground text-left">
                    <th className="pb-2 pr-4 font-medium">Type</th>
                    <th className="pb-2 pr-4 font-medium">Sub-type</th>
                    <th className="pb-2 pr-4 font-medium">Size</th>
                    <th className="pb-2 pr-4 font-medium">Face / Thread</th>
                    <th className="pb-2 pr-4 font-medium">Description</th>
                    <th className="pb-2 pr-4 font-medium">Manufacturer</th>
                    <th className="pb-2 pr-4 font-medium text-right">Unit Cost</th>
                    <th className="pb-2 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {filteredPricing.length > 0 ? filteredPricing.map((p) => (
                    <tr key={p.id} className="border-b border-border/50 hover:bg-muted/30">
                      <td className="py-2.5 pr-4 text-foreground">{TYPE_LABELS[p.type]}</td>
                      <td className="py-2.5 pr-4 text-foreground">{p.subType || "—"}</td>
                      <td className="py-2.5 pr-4 text-foreground">{p.diameter || "—"}</td>
                      <td className="py-2.5 pr-4 text-foreground">{p.faceDesign || "—"}{p.retrac ? " · Retrac" : ""}</td>
                      <td className="py-2.5 pr-4 text-foreground">{p.description}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{p.manufacturer || "—"}</td>
                      <td className="py-2.5 pr-4 text-foreground text-right font-medium">
                        ${Number(p.unitCost).toLocaleString()}
                      </td>
                      <td className="py-2.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => handleDelete(p.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                        No entries match the current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-6">
              No pricing entries yet. Add one above.
            </p>
          )}
        </CardContent>
      </Card>
      {/* Assets */}
      <Card className="bg-card border-border mt-6">
        <CardHeader>
          <CardTitle className="text-foreground">Assets</CardTitle>
          <p className="text-sm text-muted-foreground">
            Register equipment assets. Asset health data (hours, service intervals) will be linked here in a future update.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* Add form */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 rounded-lg border border-border bg-background">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Type</Label>
              <Select
                value={assetForm.type}
                onValueChange={(v) => setAssetForm((f) => ({ ...f, type: v as AssetType, model: "" }))}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ASSET_TYPE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Model</Label>
              {ASSET_MODELS[assetForm.type].length > 0 ? (
                <Select
                  value={assetForm.model}
                  onValueChange={(v) => setAssetForm((f) => ({ ...f, model: v }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select model" />
                  </SelectTrigger>
                  <SelectContent>
                    {ASSET_MODELS[assetForm.type].map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  className="h-9"
                  placeholder="e.g. CAT 390F"
                  value={assetForm.model}
                  onChange={(e) => setAssetForm((f) => ({ ...f, model: e.target.value }))}
                />
              )}
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Asset Number</Label>
              <Input
                className="h-9"
                placeholder="e.g. RIG-D65-01"
                value={assetForm.assetNumber}
                onChange={(e) => setAssetForm((f) => ({ ...f, assetNumber: e.target.value }))}
              />
            </div>

            <div className="flex items-end">
              <Button size="sm" onClick={handleAddAsset} disabled={!assetForm.assetNumber}>
                <Plus className="h-4 w-4 mr-2" />
                Add Asset
              </Button>
            </div>
          </div>

          {/* Assets table */}
          {assets.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-muted-foreground text-left">
                    <th className="pb-2 pr-4 font-medium">Type</th>
                    <th className="pb-2 pr-4 font-medium">Model</th>
                    <th className="pb-2 pr-4 font-medium">Asset Number</th>
                    <th className="pb-2 pr-4 font-medium text-muted-foreground/50">Health Data</th>
                    <th className="pb-2 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {assets.map((a) => (
                    <tr key={a.id} className="border-b border-border/50 hover:bg-muted/30">
                      <td className="py-2.5 pr-4 text-foreground">{ASSET_TYPE_LABELS[a.type]}</td>
                      <td className="py-2.5 pr-4 text-foreground">{a.model || "—"}</td>
                      <td className="py-2.5 pr-4 text-foreground font-mono">{a.assetNumber}</td>
                      <td className="py-2.5 pr-4">
                        <span className="text-xs text-muted-foreground/40 italic">Coming soon — hours, service intervals</span>
                      </td>
                      <td className="py-2.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => handleDeleteAsset(a.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-6">
              No assets registered yet. Add one above.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Bulk Upload Dialog */}
      <Dialog open={showBulkUpload} onOpenChange={(open) => { setShowBulkUpload(open); if (!open) setSelectedFile(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TableProperties className="h-5 w-5 text-primary" />
              Bulk Upload Consumable Pricing
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              Upload a CSV file to import multiple pricing entries. Existing entries are kept — new rows are appended.
            </p>
          </DialogHeader>

          {/* Template download */}
          <div className="flex items-center justify-between bg-muted/40 rounded-lg px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Download className="h-4 w-4" />
              Download template with sample data
            </div>
            <Button variant="outline" size="sm" onClick={downloadPricingTemplate}>
              <Download className="h-3.5 w-3.5 mr-1" />
              Template
            </Button>
          </div>

          {/* File drop zone */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="w-full border-2 border-dashed border-border rounded-lg py-10 flex flex-col items-center gap-2 hover:border-primary/50 transition-colors"
          >
            <Upload className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium text-foreground">
              {selectedFile ? selectedFile.name : "Click to upload CSV file"}
            </p>
            <p className="text-xs text-muted-foreground">.csv files only</p>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => { setSelectedFile(e.target.files?.[0] ?? null); e.target.value = ""; }}
          />

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => { setShowBulkUpload(false); setSelectedFile(null); }}>
              Cancel
            </Button>
            <Button onClick={() => void handleBulkImport()} disabled={!selectedFile}>
              <Upload className="h-4 w-4 mr-1.5" />
              Import
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
